"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { TruthTableView } from "@/components/tools/algebra/truth-table-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  analyse,
  buildTruthTable,
  canonicalCnf,
  canonicalDnf,
  compareFormulas,
  formatFormula,
  type LogicErrorCode,
  type NotationStyle,
  parseFormula,
  type RowOrder,
  truthVector,
} from "@/lib/tool-kit/algebra/logic";

/** English strings. A translated content file overrides these through `ui`. */
export const TRUTH_TABLE_GENERATOR_STRINGS = {
  formula: "Formula",
  formulaHint:
    "Use ¬ ∧ ∨ → ↔ ⊕, or words (not, and, or, xor, nand, nor, implies, iff), or & | ! -> <->, or Boolean algebra (AB + A′C). Brackets are always allowed.",
  examplesLabel: "Try an example",
  exampleLabels: [
    "Implication",
    "Contrapositive",
    "De Morgan",
    "Exclusive or",
    "Contradiction",
    "Syllogism",
    "Boolean",
  ],
  examples: [
    "p → q",
    "(p → q) ↔ (¬q → ¬p)",
    "¬(p ∧ q) ↔ (¬p ∨ ¬q)",
    "(p ∨ q) ∧ ¬(p ∧ q)",
    "p ∧ ¬p",
    "((p → q) ∧ (q → r)) → (p → r)",
    "AB + A′C",
  ],
  compare: "Compare with (optional)",
  compareHint:
    "A second formula: the tool says whether the two are logically equivalent.",
  symbols: "Values shown as",
  symbolOptions: { tf: "T and F", bits: "1 and 0" },
  order: "Row order",
  orders: { "true-first": "All true first", "false-first": "All false first" },
  notation: "Notation in headings",
  notations: { logic: "Logic: ¬ ∧ ∨", boolean: "Boolean: ′ · +" },
  intermediate: "Show a column for every sub-formula",
  precedenceSummary: "Operators and precedence",
  precedenceNote:
    "Tightest first. Same-level operators are read left to right, except → which is read right to left: p → q → r means p → (q → r). NAND and NOR cannot be chained without brackets.",
  precedenceHead: ["Level", "Operation", "Written as"],
  precedenceOps: [
    "NOT",
    "AND, NAND",
    "XOR",
    "OR, NOR",
    "Implication",
    "Biconditional",
  ],
  precedenceWritten: [
    "¬  ~  !  not  or a trailing ′",
    "∧  &  ·  *  and  nand  ↑  (or two operands side by side)",
    "⊕  xor",
    "∨  |  +  or  nor  ↓",
    "→  ->  =>  implies",
    "↔  <->  <=>  iff  ≡",
  ],
  results: "Truth table",
  verdict: "Verdict",
  verdicts: {
    tautology: "Tautology",
    contradiction: "Contradiction",
    contingent: "Contingent",
  },
  trueRows: "True rows",
  trueRowsValue: "{count} of {total}",
  variables: "Variables",
  verdictNotes: {
    tautology:
      "True in every row: the formula is always true, whatever the variables are.",
    contradiction: "False in every row: the formula can never be true.",
    contingent:
      "True in some rows and false in others: it is satisfiable but not always true.",
  },
  readAs: "Read as: {formula}",
  tableCaption: "Truth table for {formula}",
  copyTable: "Copy table",
  copied: "Copied!",
  formsHeading: "Normal forms",
  minterms: "True rows (minterms), Σm",
  maxterms: "False rows (maxterms), ΠM",
  minterNote:
    "Row numbers count the variables as a binary number, with the first variable as the leading digit and true as 1.",
  dnf: "Canonical disjunctive normal form (sum of minterms)",
  cnf: "Canonical conjunctive normal form (product of maxterms)",
  none: "none",
  truncated: "and {count} more",
  copyForm: "Copy",
  compareHeading: "Comparison",
  equivalent: "Equivalent: both formulas have the same value in every row.",
  notEquivalent:
    "Not equivalent. They first differ when {assignment}: the first formula is {first} and the second is {second}.",
  assignmentPart: "{name} = {value}",
  trueWord: "true",
  falseWord: "false",
  enter: "Enter a formula to see its truth table.",
  errors: {
    empty: "Enter a formula to see its truth table.",
    "too-long": "That formula is too long. Shorten it and try again.",
    character:
      "The character “{token}” is not a logical operator or a letter. Check for a typo.",
    caret:
      "The caret ^ is ambiguous: some books use it for AND and some programming languages for XOR. Write ∧, &, and or · for AND, and ⊕ or xor for XOR.",
    unexpected:
      "Something is out of place near “{token}”. Check for a missing variable or operator.",
    end: "The formula ends too soon. Check for a trailing operator.",
    bracket: "The brackets do not match.",
    ambiguous:
      "{token} is not associative, so a chain of them needs brackets: write (p {token} q) {token} r or p {token} (q {token} r).",
    "too-many-variables":
      "Use at most {token} different variables (the table has 2ⁿ rows).",
  },
} as const;

type Strings = typeof TRUTH_TABLE_GENERATOR_STRINGS;
type Symbols = keyof Strings["symbolOptions"];

const MAX_LISTED = 32;

function listOf(numbers: number[], none: string): string {
  return numbers.length === 0 ? none : numbers.join(", ");
}

function FormRow({
  label,
  text,
  strings,
}: {
  label: string;
  text: string;
  strings: Strings;
}) {
  return (
    <div className={`${RESULT_CARD} space-y-2`}>
      <p className="font-medium text-foreground text-sm">{label}</p>
      <p className="break-words font-mono text-foreground text-sm">{text}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyForm}
        small
        text={text}
      />
    </div>
  );
}

function truncated(
  text: string[],
  separator: string,
  strings: Strings
): string {
  if (text.length <= MAX_LISTED) {
    return text.join(separator);
  }
  return `${text.slice(0, MAX_LISTED).join(separator)}${separator}… (${t(strings.truncated, { count: text.length - MAX_LISTED })})`;
}

function Results({
  text,
  other,
  options,
  strings,
}: {
  text: string;
  other: string;
  options: {
    symbols: Symbols;
    order: RowOrder;
    style: NotationStyle;
    intermediate: boolean;
  };
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const parsed = parseFormula(text);
  if (!parsed.ok) {
    return (
      <ErrorNotice>
        {t(strings.errors[parsed.error.code as LogicErrorCode], {
          token: parsed.error.token,
        })}
      </ErrorNotice>
    );
  }
  const { formula, variables } = parsed;
  const table = buildTruthTable(formula, variables, options);
  const vector = truthVector(formula, variables);
  const analysis = analyse(vector);
  const [yes, no] = options.symbols === "tf" ? ["T", "F"] : ["1", "0"];
  const reading = formatFormula(formula, options.style, true);
  const sentenceTable = [
    [...variables, ...table.columns.map((c) => c.label)].join("\t"),
    ...table.rows.map((row) =>
      [...row.inputs, ...row.values].map((v) => (v ? yes : no)).join("\t")
    ),
  ].join("\n");
  const dnf = canonicalDnf(variables, analysis.minterms, options.style);
  const cnf = canonicalCnf(variables, analysis.maxterms, options.style);
  const dnfTerms = dnf.split(options.style === "boolean" ? " + " : " ∨ ");
  const cnfTerms = cnf.split(options.style === "boolean" ? "·" : " ∧ ");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.verdict}
          value={strings.verdicts[analysis.kind]}
        />
        <StatCard
          label={strings.trueRows}
          value={t(strings.trueRowsValue, {
            count: fmt(analysis.trueRows),
            total: fmt(analysis.totalRows),
          })}
        />
        <StatCard
          label={strings.variables}
          value={variables.length === 0 ? "0" : variables.join(", ")}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">
        {strings.verdictNotes[analysis.kind]}
      </p>
      <p className="font-mono text-foreground text-sm">
        {t(strings.readAs, { formula: reading })}
      </p>
      <TruthTableView
        caption={t(strings.tableCaption, { formula: reading })}
        falseSymbol={no}
        table={table}
        trueSymbol={yes}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyTable}
        small
        text={sentenceTable}
      />
      <h3 className={WIDGET_H3}>{strings.formsHeading}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormRow
          label={strings.minterms}
          strings={strings}
          text={listOf(analysis.minterms, strings.none)}
        />
        <FormRow
          label={strings.maxterms}
          strings={strings}
          text={listOf(analysis.maxterms, strings.none)}
        />
      </div>
      <p className="text-muted-foreground text-xs">{strings.minterNote}</p>
      <FormRow
        label={strings.dnf}
        strings={strings}
        text={
          dnfTerms.length > MAX_LISTED
            ? truncated(
                dnfTerms,
                options.style === "boolean" ? " + " : " ∨ ",
                strings
              )
            : dnf
        }
      />
      <FormRow
        label={strings.cnf}
        strings={strings}
        text={
          cnfTerms.length > MAX_LISTED
            ? truncated(
                cnfTerms,
                options.style === "boolean" ? "·" : " ∧ ",
                strings
              )
            : cnf
        }
      />
      {other.trim() === "" ? null : (
        <Comparison first={formula} other={other} strings={strings} />
      )}
    </>
  );
}

function Comparison({
  first,
  other,
  strings,
}: {
  first: Parameters<typeof compareFormulas>[0];
  other: string;
  strings: Strings;
}) {
  const parsed = parseFormula(other);
  if (!parsed.ok) {
    return (
      <ErrorNotice>
        {t(strings.errors[parsed.error.code as LogicErrorCode], {
          token: parsed.error.token,
        })}
      </ErrorNotice>
    );
  }
  const result = compareFormulas(first, parsed.formula);
  const example = result.counterexample;
  const word = (value: boolean) =>
    value ? strings.trueWord : strings.falseWord;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      {result.equivalent || !example ? (
        <Notice>{strings.equivalent}</Notice>
      ) : (
        <Notice>
          {t(strings.notEquivalent, {
            assignment: result.variables
              .map((name) =>
                t(strings.assignmentPart, {
                  name,
                  value: word(example.assignment[name]),
                })
              )
              .join(", "),
            first: word(example.first),
            second: word(example.second),
          })}
        </Notice>
      )}
    </div>
  );
}

export default function TruthTableGeneratorWidget() {
  const strings = useToolUi(TRUTH_TABLE_GENERATOR_STRINGS);
  const [formula, setFormula] = useState<string>(strings.examples[1]);
  const [other, setOther] = useState("");
  const [symbols, setSymbols] = useState<Symbols>("tf");
  const [order, setOrder] = useState<RowOrder>("true-first");
  const [style, setStyle] = useState<NotationStyle>("logic");
  const [intermediate, setIntermediate] = useState(true);
  return (
    <>
      <TextField
        hint={strings.formulaHint}
        id="ttg-formula"
        label={strings.formula}
        mono
        onChange={setFormula}
        value={formula}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">
          {strings.examplesLabel}
        </span>
        {strings.exampleLabels.map((label, i) => (
          <button
            className={BUTTON_SMALL}
            key={label}
            onClick={() => setFormula(strings.examples[i] ?? "")}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>
      <TextField
        hint={strings.compareHint}
        id="ttg-other"
        label={strings.compare}
        mono
        onChange={setOther}
        value={other}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <PillGroup
          label={strings.symbols}
          name="ttg-symbols"
          onChange={setSymbols}
          options={(["tf", "bits"] as const).map((value) => ({
            value,
            label: strings.symbolOptions[value],
          }))}
          value={symbols}
        />
        <PillGroup
          label={strings.order}
          name="ttg-order"
          onChange={setOrder}
          options={(["true-first", "false-first"] as const).map((value) => ({
            value,
            label: strings.orders[value],
          }))}
          value={order}
        />
        <PillGroup
          label={strings.notation}
          name="ttg-notation"
          onChange={setStyle}
          options={(["logic", "boolean"] as const).map((value) => ({
            value,
            label: strings.notations[value],
          }))}
          value={style}
        />
      </div>
      <Checkbox
        checked={intermediate}
        label={strings.intermediate}
        onChange={setIntermediate}
      />
      <details className="rounded-lg border border-border bg-card p-4">
        <summary className="cursor-pointer font-medium text-foreground text-sm">
          {strings.precedenceSummary}
        </summary>
        <div className="mt-3 space-y-2">
          <DataTable
            head={strings.precedenceHead}
            rows={strings.precedenceOps.map((op, i) => [
              i + 1,
              op,
              strings.precedenceWritten[i] ?? "",
            ])}
          />
          <p className="text-muted-foreground text-xs">
            {strings.precedenceNote}
          </p>
        </div>
      </details>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {formula.trim() === "" ? (
          <Notice>{strings.enter}</Notice>
        ) : (
          <Results
            options={{ symbols, order, style, intermediate }}
            other={other}
            strings={strings}
            text={formula}
          />
        )}
      </section>
    </>
  );
}
